import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Spinner } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import "../../styles/Article.css";
import { supabase } from "../../config/Supabase";
import SEO from "../utils/SEO";

// "View All" landing page for a category: /learn/category/<category>.
// Lists every published article in that category from Supabase.

const CATEGORY_LABELS = {
  herbalism: "Basics of Medicinal Herbs",
  recipes: "Everyday Recipes",
  "mental-health": "Mental Health",
  "digestive-health": "Digestive Health",
  ayurveda: "Ayurveda",
  "womens-health": "Women's Health",
  "weight-loss": "Weight Loss",
  "heart-health": "Heart Health",
  diabetes: "Diabetes",
  cancer: "Cancer",
};
const labelFor = (c) =>
  CATEGORY_LABELS[c] ||
  (c ? c.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) : "Articles");

const truncate = (t, n) => (t && t.length > n ? `${t.slice(0, n)}…` : t || "");

const CategoryPage = () => {
  const { category } = useParams();
  const [articles, setArticles] = useState([]);
  const [catName, setCatName] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const [{ data: arts }, { data: cat }] = await Promise.all([
        supabase
          .from("articles")
          .select("slug,title,summary,hero_image_url")
          .eq("status", "published")
          .eq("category", category)
          .order("published_at", { ascending: false, nullsFirst: false }),
        supabase.from("article_categories").select("name").eq("slug", category).maybeSingle(),
      ]);
      if (!active) return;
      if (arts) setArticles(arts);
      if (cat) setCatName(cat.name);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [category]);

  const title = catName || labelFor(category);

  return (
    <Container>
      <SEO
        title={`${title} - Sarv Health Library`}
        description={`Explore Sarv's articles on ${title.toLowerCase()} — evidence-based information on medicinal herbs, natural remedies, and holistic health.`}
        canonicalPath={`/learn/category/${category}`}
        keywords={`${title}, medicinal herbs, natural remedies, holistic health, herbal medicine articles`}
      />
      <Row
        className="mb-3 justify-content-center"
        style={{ alignItems: "center", paddingTop: "100px", paddingBottom: "50px" }}
      >
        <Col>
          <h1 className="article-categoories-title text-center">{title}</h1>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center" style={{ paddingBottom: "100px" }}>
          <Spinner animation="border" />
        </div>
      ) : articles.length === 0 ? (
        <p className="text-center" style={{ color: "#777", paddingBottom: "100px" }}>
          No articles in this category yet. <Link to="/learn">Back to the library</Link>.
        </p>
      ) : (
        <Row>
          {articles.map((article) => (
            <Col xs={12} md={4} key={article.slug}>
              <Link
                to={`/learn/post/${article.slug}`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Card style={{ border: "none", borderRadius: "15px", marginBottom: "50px", padding: "15px" }}>
                  <Card.Img
                    variant="top"
                    src={article.hero_image_url || "/img/og-image.jpg"}
                    alt={article.title}
                    loading="lazy"
                    style={{
                      height: "200px",
                      objectFit: "cover",
                      borderTopLeftRadius: "15px",
                      borderTopRightRadius: "15px",
                    }}
                  />
                  <Card.Body>
                    <Card.Title>{article.title}</Card.Title>
                    <Card.Text>{truncate(article.summary, 100)}</Card.Text>
                  </Card.Body>
                </Card>
              </Link>
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
};

export default CategoryPage;
